import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {SettingENUM} from '../enums/setting.enum';
import {Principal} from './principal';
import {Utils} from './Utils';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

@Injectable()
export class AuthService {

  constructor(private _http: HttpClient,
              private _translateService: TranslateService,
              private _activatedRoute: ActivatedRoute,
              private _router: Router,
              private _principal: Principal,
              private _utils: Utils) {
  }

  logout(personTypeID) {
    this._http.get('api/user/logout').subscribe(() => {
      // console.log('user logout');
    });
    this._principal.deleteTokenAndUser();
    this._router.navigate(['/']);
  }

  login(data) {
    this._principal.deleteTokenAndUser();
    this._utils.blockStart('checking_login_password');

    const request = {
      grant_type: 'password',
      username: data.login,
      password: data.password
    };

    const encoded = btoa(`${SettingENUM.CLIENT_ID}:${SettingENUM.CLIENT_SECRET}`);
    const headers = new HttpHeaders({
      'Authorization': `Basic ${encoded}`,
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      'noToken': 'true' // for interceptor
    });

    // Dynamically serialize the entire object
    // *** THIS IS THE SERIALIZATION ***
    const params: any = AuthService.serialize(request);
    return this._http
      .post(`oauth/token`, params.toString(), {headers: headers})
      .subscribe(
        res => {
          this._principal.deleteTokenAndUser();
          this.getUserDto(res['access_token']);
        },
        err => {
          let errorMessage = this._translateService.instant('error_occurred');
          if (err.status === 400 || err.status === 401) {
            errorMessage = this._translateService.instant('wrong_auth_data');
            if (err.error.error_description === 'Access not available') {
              errorMessage = this._translateService.instant('access_not_available');
            } else if (err.error.error_description === 'blocked') {
              errorMessage = this._translateService.instant('access_blocked');
            }
          } else if (err.status === 504 || err.status === 404) {
            errorMessage = this._translateService.instant('server_unavailable');
          }
          // console.log(params.toString());
          // console.log(err);
          this._utils.blockStop();
          this._utils.notifyError(errorMessage, null);
        }
      );
  }

  loginEnroll(data) {
    this._principal.deleteTokenAndUser();
    this._utils.blockStart('checking_login_password');

    const request = {
      grant_type: 'password',
      username: data.login,
      password: data.password,
      college: data.college
    };

    const encoded = btoa(`${SettingENUM.ENROLL_CLIENT_ID}`);
    const headers = new HttpHeaders({
      'encodedClientId': `${encoded}`,
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      'noToken': 'true' // for interceptor
    });

    // Dynamically serialize the entire object
    // *** THIS IS THE SERIALIZATION ***
    const params: any = AuthService.serialize(request);

    return this._http.post(`api/user/enroll/login`, params.toString(), {headers: headers}).subscribe(
      res => {
        this._principal.deleteTokenAndUser();
        this.getUserDto(res['access_token']);
      },
      err => {
        let errorMessage = this._translateService.instant('error_occurred');
        if (err.status === 400 || err.status === 401) {
          errorMessage = this._translateService.instant('wrong_auth_data');
          if (err.error.error_description === 'Access not available') {
            errorMessage = this._translateService.instant('access_not_available');
          } else if (err.error.error_description === 'blocked') {
            errorMessage = this._translateService.instant('access_blocked');
          }
        } else if (err.status === 504 || err.status === 404) {
          errorMessage = this._translateService.instant('server_unavailable');
        }
        // console.log(params.toString());
        // console.log(err);
        this._utils.blockStop();
        this._utils.notifyError(errorMessage, null);
      }
    );
  }

  getEnrollInfo(userName) {
    const request = {
      username: userName
    };
    const encoded = btoa(`${SettingENUM.CLIENT_ID}:${SettingENUM.CLIENT_SECRET}`);
    const headers = new HttpHeaders({
      'Authorization': `Basic ${encoded}`,
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      'noToken': 'true' // for interceptor
    });
    const params: any = AuthService.serialize(request);
    return this._http.post<any>(`api/user/enroll/getInfo`,  params.toString(),{headers: headers});
  }

  getUserDto(token: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'noToken': 'true'
    });
    return this._http.get(`api/user/current`, {headers: headers}).subscribe(
      userDTO => {
        this._principal.setTokenAndUser(token, userDTO);
        let returnUrl = this._activatedRoute.snapshot.queryParams['returnUrl'];

        if (!returnUrl) {
          returnUrl = '';
        }
        if (this._principal.isAdmin() || this._principal.isRegionAdmin()) {
          if (returnUrl.indexOf('admin') == -1) {
            returnUrl = '/admin';
          }
        } else if (this._principal.isModerator()) {
          if (returnUrl.indexOf('moderator') == -1) {
            returnUrl = '/moderator/main';
          }
        } else if (this._principal.isUser()) {
          const user = this._principal.getUser();
          if (user.passwordOneTimeGenerated && !user.hasLoggedInAfterPasswordGenerate) {
            returnUrl = '/user/authorization-parameter';
          } else if (user.passwordOneTimeGenerated && user.hasLoggedInAfterPasswordGenerate) {
            this._utils.notifyError(this._translateService.instant('password_generate.logged_in_already'), null);
            this._principal.deleteTokenAndUser();
            returnUrl = '/';
          } else if (returnUrl.indexOf('user') == -1) {
            returnUrl = '/user';
          }
        } else if (this._principal.isEnroll()) {
          returnUrl = '/enroll/cabinet';
        }
        // console.log('returnUrl', returnUrl);
        this._router.navigateByUrl(returnUrl);

        setTimeout(() => {
          this._utils.blockStop();
        }, 1000);
      },
      err => {
        console.log(err);
        this._utils.blockStop();
        this._utils.notifyError('error_occurred', null);
      }
    );
  }

  static serialize(obj: any): any {
    const params: URLSearchParams = new URLSearchParams();
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const element = obj[key];
        params.set(key, element);
      }
    }
    return params;
  }
}



// WEBPACK FOOTER //
// ./src/app/common/services/auth.service.ts